import { getGrassCharacteristics } from '@/data/grass-characteristics'
import type { CanonicalLawnProfile, InputEffect, SunlightBand } from './types'
import { quantitySpec, makeSourced, type SourcedQuantity } from './quantity-sources'

// Sourced (lib/lawn-plan-engine/quantity-sources.ts) — no longer a bare literal.
const WATER_GALLONS_PER_SQFT_INCH = quantitySpec('water_gallonsPerSqftInch').value

const SUNLIGHT_RULES: Record<SunlightBand, {
  label: string
  directSunHours: string
  waterMultiplier: number
  nitrogenMultiplier: number
  mowingRaiseInches: number
  sourceIds: string[]
}> = {
  full_sun: {
    label: 'Full sun',
    directSunHours: '6+ hours direct sun',
    waterMultiplier: 1,
    nitrogenMultiplier: 1,
    mowingRaiseInches: 0,
    sourceIds: ['okstate-shade-2026', 'umd-established-lawn-2026'],
  },
  partial_sun: {
    label: 'Partial sun',
    directSunHours: '3 to 6 hours direct sun',
    waterMultiplier: 0.9,
    nitrogenMultiplier: 0.8,
    mowingRaiseInches: 0.25,
    sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026'],
  },
  shade: {
    label: 'Mostly shade',
    directSunHours: 'Under 3 hours direct sun',
    waterMultiplier: 0.8,
    nitrogenMultiplier: 0.5,
    mowingRaiseInches: 0.5,
    sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026', 'missouri-shade-2026'],
  },
}

export function sunlightRule(sunlight?: SunlightBand | null) {
  return sunlight ? SUNLIGHT_RULES[sunlight] : null
}

export function sunlightReference() {
  return SUNLIGHT_RULES
}

function round(value: number, digits = 2): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

function parseHeightRange(raw?: string | null): { min: number | null; max: number | null } {
  const values = (raw ?? '').match(/\d+(?:\.\d+)?/g)?.map(Number).filter(Number.isFinite) ?? []
  if (values.length === 0) return { min: null, max: null }
  return { min: Math.min(...values), max: Math.max(...values) }
}

export function adjustWaterNeedForSunlight(baseInches: number, sunlight?: SunlightBand | null): number {
  const rule = sunlightRule(sunlight)
  if (!rule) return round(baseInches)
  return round(Math.max(0, baseInches * rule.waterMultiplier))
}

export function adjustNitrogenForSunlight(baseRateLbsN: number, sunlight?: SunlightBand | null): number {
  const rule = sunlightRule(sunlight)
  if (!rule) return round(baseRateLbsN, 2)
  return round(Math.max(0, baseRateLbsN * rule.nitrogenMultiplier), 2)
}

export function adjustMowingTargetForSunlight(
  targetInches: number,
  grassType: string,
  sunlight?: SunlightBand | null,
): number {
  const rule = sunlightRule(sunlight)
  if (!rule || rule.mowingRaiseInches <= 0) return round(targetInches, 1)
  const grass = getGrassCharacteristics(grassType)
  const max = parseHeightRange(grass?.mowingDetails?.maximum ?? grass?.mowingHeight).max
  const adjusted = targetInches + rule.mowingRaiseInches
  return round(max ? Math.min(adjusted, max) : adjusted, 1)
}

export function gallonsForInches(sqft: number, inches: number): number {
  return Math.round(sqft * WATER_GALLONS_PER_SQFT_INCH * inches)
}

export function formatQuantityForSqft(ratePer1000Sqft: number, sqft: number | null, unit: string): string | null {
  if (!sqft || sqft <= 0) return null
  const total = round((ratePer1000Sqft * sqft) / 1000, 1)
  if (total <= 0) return null
  return `About ${total} ${unit} for your ${sqft.toLocaleString()} sq ft lawn (${ratePer1000Sqft} per 1,000 sq ft).`
}

export function fertilizerProductPoundsForNitrogen(input: {
  targetNitrogenLbsPer1000: number
  nitrogenPercent: number
  sqft: number
}): number {
  if (input.targetNitrogenLbsPer1000 <= 0 || input.nitrogenPercent <= 0 || input.sqft <= 0) return 0
  const productLbsPer1000 = input.targetNitrogenLbsPer1000 / (input.nitrogenPercent / 100)
  return round(productLbsPer1000 * (input.sqft / 1000), 1)
}

function grassShadeMismatchEffect(profile: CanonicalLawnProfile): InputEffect | null {
  if (!profile.sunlight || profile.sunlight === 'full_sun') return null
  const grass = getGrassCharacteristics(profile.grassType)
  if (!grass || grass.shadeTolerance !== 'Low') return null
  return {
    input: 'sunlight',
    kind: 'grass_shade_mismatch',
    effect: `${profile.grassType} has low shade tolerance, so the plan flags shade stress instead of promising that extra products will fix the site mismatch.`,
    affectedOutputs: ['watch', 'mowing', 'fertilizing'],
    confidence: profile.sunlight === 'shade' ? 'high' : 'medium',
    sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026'],
  }
}

export function buildInputEffects(input: {
  profile: CanonicalLawnProfile
  hasWeather: boolean
  hasHistory: boolean
}): InputEffect[] {
  const effects: InputEffect[] = []
  const { profile } = input

  if (profile.grassTypeResolution.isKnown) {
    effects.push({
      input: 'grass_type',
      effect: `${profile.grassType} drives season, mowing height, water target, fertilizer timing, and disease-risk windows.`,
      affectedOutputs: ['do', 'dont', 'watch', 'waterBalance', 'mowing'],
      confidence: profile.grassTypeResolution.confidence,
      sourceIds: profile.grassTypeResolution.sourceIds,
    })
  } else if (profile.grassTypeResolution.ambiguous) {
    effects.push({
      input: 'grass_type',
      effect: `${profile.grassTypeRaw ?? 'The saved grass type'} is ambiguous, so the engine uses starter-safe guidance until Tall Fescue, Fine Fescue, or another exact type is selected.`,
      affectedOutputs: ['watch', 'mowing', 'fertilizing'],
      confidence: 'high',
      sourceIds: profile.grassTypeResolution.sourceIds,
    })
  } else {
    effects.push({
      input: 'grass_type',
      effect: 'No exact grass type is available, so the engine avoids species-specific chemical timing and asks for a better grass ID.',
      affectedOutputs: ['watch', 'mowing', 'fertilizing'],
      confidence: 'high',
      sourceIds: profile.grassTypeResolution.sourceIds,
    })
  }

  if (profile.sunlight) {
    const rule = sunlightRule(profile.sunlight)!
    effects.push({
      input: 'sunlight',
      effect:
        profile.sunlight === 'full_sun'
          ? 'Full sun keeps the default water, nitrogen, and mowing assumptions for the grass type.'
          : `${rule.label} lowers routine water and nitrogen intensity, raises mowing guidance where useful, and increases shade-stress watch items.`,
      affectedOutputs: ['waterBalance', 'mowing', 'dont', 'watch'],
      confidence: 'high',
      sourceIds: rule.sourceIds,
    })
  } else {
    effects.push({
      input: 'sunlight',
      effect: 'Sun exposure is missing, so the engine keeps default full-site assumptions and lowers confidence.',
      affectedOutputs: ['waterBalance', 'mowing', 'confidence'],
      confidence: 'medium',
      sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026'],
    })
  }

  const mismatch = grassShadeMismatchEffect(profile)
  if (mismatch) effects.push(mismatch)

  if (profile.lawnSizeSqft) {
    effects.push({
      input: 'lawn_size',
      effect: `Square footage does not change the biology, but it scales gallons, product amounts, time, cost, and equipment practicality for ${profile.lawnSizeSqft.toLocaleString()} sq ft.`,
      affectedOutputs: ['quantityHint', 'waterBalance', 'products'],
      confidence: profile.lawnSizeResolution.confidence,
      sourceIds: profile.lawnSizeResolution.sourceIds,
    })
  } else {
    effects.push({
      input: 'lawn_size',
      effect: 'Square footage is missing, so the engine can recommend what to do but cannot give exact gallons or product amounts.',
      affectedOutputs: ['quantityHint', 'confidence'],
      confidence: 'high',
      sourceIds: ['rutgers-fertilizer-math-2026'],
    })
  }

  if (profile.zones.length > 0) {
    effects.push({
      input: 'zones',
      effect: 'Enabled zone-level grass, sunlight, soil, and area values override whole-lawn defaults for zone-specific watch items.',
      affectedOutputs: ['watch', 'mowing', 'fertilizing'],
      confidence: 'medium',
      sourceIds: ['okstate-shade-2026', 'rutgers-fertilizer-math-2026'],
    })
  }

  if (input.hasWeather) {
    effects.push({
      input: 'weather',
      effect: 'Rain, forecast, temperature, humidity, wind, and soil temperature adjust timing and safety holds deterministically.',
      affectedOutputs: ['do', 'dont', 'watch', 'waterBalance'],
      confidence: 'high',
      sourceIds: ['umd-established-lawn-2026'],
    })
  }

  if (input.hasHistory) {
    effects.push({
      input: 'history',
      effect: 'Recent journal and product-memory entries prevent repeat fertilizer, stacked herbicide, and stale application advice.',
      affectedOutputs: ['dont', 'watch'],
      confidence: 'high',
      sourceIds: ['purdue-fertilizer-2026'],
    })
  }

  return effects
}


// ── Quantitative safety (Phase 6) ───────────────────────────────────────────
// Hard over-application ceiling for N, P AND K. Previously only N was hard-capped
// (fertilizer-calculator) while P/K were warn-only, so a poor NPK ratio could
// silently over-deliver phosphorus or potassium (runoff / nutrient-loading risk).
// POLICY: no nutrient may exceed `overApply_capMultiplier`× its agronomic target
// (sourced in quantity-sources; currently 1.5× — a burn/runoff guard, matching the
// prior nitrogen-only cap, NOT a zero-headroom cap). We accept UNDER-delivery of
// the limiting nutrient rather than exceeding any nutrient's ceiling, since
// over-application is the worse failure mode (turf burn, runoff).

export interface NutrientCapResult {
  /** Pounds of product after capping (rounded to 0.1). */
  poundsNeeded: number
  /** Informational "X% more than needed" notes against the FINAL pounds. */
  warnings: string[]
  /** Which nutrients forced a cap. */
  capsApplied: Array<'nitrogen' | 'phosphorus' | 'potassium'>
}

export function applyNutrientSafetyCaps(input: {
  poundsNeeded: number
  npkRatio: { nitrogen: number; phosphorus: number; potassium: number } // percentages
  target: { nitrogen: number; phosphorus: number; potassium: number } // total lbs for the lawn
  capMultiplier?: number
}): NutrientCapResult {
  const cap = input.capMultiplier ?? quantitySpec('overApply_capMultiplier').value
  const nutrients: Array<'nitrogen' | 'phosphorus' | 'potassium'> = ['nitrogen', 'phosphorus', 'potassium']
  const capsApplied: NutrientCapResult['capsApplied'] = []
  // Defensive: never propagate a bad caller value into a recommended quantity.
  let pounds = Number.isFinite(input.poundsNeeded) ? Math.max(0, input.poundsNeeded) : 0

  for (const key of nutrients) {
    const ratio = input.npkRatio[key]
    const target = input.target[key]
    if (ratio <= 0 || target <= 0) continue
    const maxPounds = (target * cap) / (ratio / 100)
    if (pounds > maxPounds) {
      pounds = maxPounds
      capsApplied.push(key)
    }
  }

  const warnings: string[] = []
  for (const key of nutrients) {
    const ratio = input.npkRatio[key]
    const target = input.target[key]
    if (ratio <= 0 || target <= 0) continue
    const delivered = pounds * (ratio / 100)
    if (delivered > target * 1.2) {
      const excess = ((delivered / target - 1) * 100).toFixed(0)
      warnings.push(`Note: This will apply ${excess}% more ${key} than needed.`)
    }
  }

  return { poundsNeeded: round(pounds, 1), warnings, capsApplied }
}

/**
 * Cited single-application nitrogen ceiling (lb N / 1000 sq ft). Conservative
 * 1.0 quick-release standard for all grasses (never over-applies). Replaces the
 * bare uncited per-app assumptions with a sourced value.
 */
export function singleAppMaxN(_grassSeason?: string | null): SourcedQuantity<number> {
  const spec = quantitySpec('singleApp_maxN_lbPer1000')
  return makeSourced(spec.value, spec.sourceIds)
}
